import { Dispatch, SetStateAction, useState } from "react";
import PopupModelWrapper from "../wrappers/PopupModelWrapper";
import { Stack, TextField, Typography } from "@mui/material";
import PopupButton from "../common/PopupButton";
import {
    preBackorderDomainInputBox,
    preBackorderInfoStyle,
    preBackorderLearnMoreStyle,
} from "./modelStyles";
import { validators } from "src/data/validators";
import psl, { ParsedDomain } from "psl";
import useMessage from "src/hooks/useMessage";
import useData from "src/hooks/useApi";
import { Order } from "./BackorderModel";
import { DropScheduleRecord, PreBackorderStatus } from "./PreBackorderModel";
import { useNavigate } from "react-router-dom";

type PreBackorderDomainInput = {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    setIsDateSelectOpen: Dispatch<SetStateAction<boolean>>;
    setDomain: React.Dispatch<React.SetStateAction<Order[]>>;
    setSchedule: React.Dispatch<React.SetStateAction<DropScheduleRecord[]>>;
};

const PreBackorderDomainInputModel = (props: PreBackorderDomainInput) => {
    const { open, setOpen, setIsDateSelectOpen, setDomain, setSchedule } =
        props;

    const { fetchData } = useData();
    const { showError } = useMessage();
    const navigate = useNavigate();

    const [domainInput, setDomainInput] = useState<string>("");
    const [error, setError] = useState<React.ReactNode>("");
    const [isScheduleLoading, setIsScheduleLoading] = useState<boolean>(false);

    const handleClose = () => {
        setError("");
        setDomainInput("");
        setOpen(false);
    };

    const moveToDateSelection = async () => {
        setIsScheduleLoading(true);
        try {
            const sanitizedDomainInput = domainInput
                .replace(/\u00A0/g, "")
                .replace(/[\s]+/g, "")
                .trim();

            if (!sanitizedDomainInput) {
                setError("Please enter a domain name.");
                return;
            }

            const isValidDomainName =
                validators.DOMAIN_NAME.test(sanitizedDomainInput);

            if (!isValidDomainName) {
                setError("Please enter a valid domain name.");
                return;
            }

            const parsedDomain = psl.parse(
                sanitizedDomainInput,
            ) as ParsedDomain;

            if (!parsedDomain || parsedDomain.error) {
                setError("Please enter a valid domain name.");
                return;
            }

            const tld = parsedDomain.tld;

            if (!tld) {
                setError(`Invalid TLD received from the domain ${domainInput}`);
                return;
            }

            const response = await fetchData(
                `/bid/getDomainPreBackorderSchedule/${sanitizedDomainInput}`,
            );
            if (!response) {
                throw new Error("Response not received");
            }
            const { status, schedule } = response;

            if (status === PreBackorderStatus.AVAILABLE) {
                setDomain([{ domainName: sanitizedDomainInput, priority: 6 }]);
                setSchedule(schedule);
                setIsDateSelectOpen(true);
                handleClose();
            }

            if (status === PreBackorderStatus.DROP_DETAILS_NOT_FOUND) {
                setError(
                    "Drop details not found. This domain is currently unavailable.",
                );
                return;
            }

            if (status === PreBackorderStatus.NOT_AVAILABLE_FOR_PRE_BACKORDER) {
                setError(
                    "Pre-backordering is not allowed as the drop window has expired. Backordering is still available.",
                );
                return;
            }

            if (status === PreBackorderStatus.NOT_SUPPORTED_TLD) {
                setError(
                    <>
                        The domain is not supported For Pre-Backordering. Check
                        the list of{" "}
                        <span
                            style={{
                                textDecoration: "underline",
                                cursor: "pointer",
                            }}
                            onClick={() => navigate("/learn-pre-backorder")}
                        >
                            supported TLDs
                        </span>
                        .
                    </>,
                );
                return;
            }

            if (status === PreBackorderStatus.FETCH_FAILED) {
                setError(
                    "Failed to fetch the schedule. Please try again later",
                );
                return;
            }
        } catch (error: any) {
            if (error.message === "Please enter a valid domain name") {
                setError(error.message);
                return;
            }
            showError("Error Occurred when fetching Prebackorder Schedule");
            handleClose();
        } finally {
            setIsScheduleLoading(false);
        }
    };

    const handleDomainInput = (e: any) => {
        const value = e.target.value.toLowerCase();
        setDomainInput(value);

        if (error) {
            setError("");
        }
    };

    return (
        <PopupModelWrapper
            open={open}
            handleClose={handleClose}
            title="Pre-Backorder Domain"
        >
            <Stack>
                <Typography
                    align="justify"
                    marginTop={3}
                    marginBottom={3}
                    sx={preBackorderInfoStyle}
                >
                    Increase your chances of securing domains with
                    Pre-Backorder.&nbsp;
                    <Typography
                        component="span"
                        sx={preBackorderLearnMoreStyle}
                        onClick={() => navigate("/learn-pre-backorder")}
                    >
                        Learn more
                    </Typography>
                </Typography>
                <Typography sx={preBackorderInfoStyle}>Domain Name</Typography>
                <TextField
                    id="domains-input-text-field"
                    variant="outlined"
                    hiddenLabel
                    placeholder="abc.info"
                    sx={preBackorderDomainInputBox}
                    value={domainInput}
                    onChange={handleDomainInput}
                    error={error !== ""}
                    helperText={error !== "" && error}
                />
                <Stack direction="row" justifyContent="flex-end">
                    <PopupButton
                        triggerFunction={handleClose}
                        type="cancel"
                        id="domains-input-popup-cancel"
                    >
                        Cancel
                    </PopupButton>
                    <PopupButton
                        triggerFunction={moveToDateSelection}
                        type="proceed"
                        id="domains-input-popup-add"
                        disabled={error !== ""}
                        isLoading={isScheduleLoading}
                    >
                        Proceed
                    </PopupButton>
                </Stack>
            </Stack>
        </PopupModelWrapper>
    );
};

export default PreBackorderDomainInputModel;
